import React, { useEffect, useRef } from 'react';
import { setupIonicReact } from '@ionic/react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';

import AppContextProvider from '@projectK/context/AppContextProvider';
import AppThemeProvider from '@projectK/context/AppThemeProvider';
import AppStyleProvider from '@projectK/context/AppStyleProvider';
import AppLocaleProvider from '@projectK/context/AppLocaleProvider';
import InfoViewContextProvider from '@projectK/context/InfoViewContextProvider';
import AppAuthProvider from './core/AppAuthProvider';
import AuthRoutes from '@projectK/components/AuthRoutes';
import AppLayout from './core/AppLayout';
import './assets/styles/maps.css';
import { VerbiageContextProvider } from '@projectK/context/VerbiageContextProvider';
import { CheckAdddonsContextProvider } from '@projectK/context/CheckAdddons';
import { CheckMissedPaymentContextProvider } from '@projectK/context/checkMissedPayments';
import { MobileContextProvider } from '@projectK/context/MobileContextProvider';
import { ToastProvider } from '@projectK/context/ToastProvider';
import { Toast } from '@capacitor/toast';
import PropTypes from 'prop-types';
import { App as CapacitorApp } from '@capacitor/app';
import { RolePermissionsProvider } from '@projectK/context/RolesProvider';
import { DashboardProvider } from '@projectK/context/DashboardProvider';

setupIonicReact();

const HardwareBackHandler = () => {
  const navigate = useNavigate();
  const backPressedOnce = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handler = CapacitorApp.addListener(
      'backButton',
      async ({ canGoBack }) => {
        if (canGoBack) {
          navigate(-1);
          return;
        }

        if (backPressedOnce.current) {
          CapacitorApp.exitApp();
          return;
        }

        backPressedOnce.current = true;
        await Toast.show({
          text: 'Press back again to exit',
          duration: 'short',
        });

        timeoutRef.current = setTimeout(() => {
          backPressedOnce.current = false;
        }, 1000);
      },
    );

    return () => {
      handler.remove();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [navigate]);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === 'Go') {
        if (document.activeElement) {
          document.activeElement.blur();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  return null;
};

const App = ({ isMobile }) => {
  return (
    <AppContextProvider>
      <AppThemeProvider>
        <AppStyleProvider>
          <AppLocaleProvider>
            <BrowserRouter>
              {isMobile && <HardwareBackHandler />}
              <InfoViewContextProvider>
                <DashboardProvider>
                  <AppAuthProvider>
                    <MobileContextProvider>
                      <AuthRoutes>
                        <VerbiageContextProvider>
                          <CheckAdddonsContextProvider>
                            <CheckMissedPaymentContextProvider>
                              <CssBaseline />
                              <ToastProvider>
                                <RolePermissionsProvider>
                                  <AppLayout />
                                </RolePermissionsProvider>
                              </ToastProvider>
                            </CheckMissedPaymentContextProvider>
                          </CheckAdddonsContextProvider>
                        </VerbiageContextProvider>
                      </AuthRoutes>
                    </MobileContextProvider>
                  </AppAuthProvider>
                </DashboardProvider>
              </InfoViewContextProvider>
            </BrowserRouter>
          </AppLocaleProvider>
        </AppStyleProvider>
      </AppThemeProvider>
    </AppContextProvider>
  );
};

export default App;
App.propTypes = {
  isMobile: PropTypes.bool,
};
